import { useState, useRef } from 'react';
import { parseUploadFile } from './parseUpload';
import { INSTRUMENTS } from './constants';

export function ConcertBulkUpload({ onSubmit }) {
  const [parsedData, setParsedData] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editingConcert, setEditingConcert] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const fileRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setParsedData(null);
    setSubmitResult(null);
    setFileName(file.name);
    setUploading(true);

    try {
      const data = await parseUploadFile(file);
      setParsedData(data);
    } catch (err) {
      setParseError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setParsedData(null);
    setParseError(null);
    setFileName('');
    setSubmitResult(null);
    setEditingCell(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const updateField = (section, idx, field, value) => {
    setParsedData((prev) => ({
      ...prev,
      [section]: prev[section].map((item, i) => {
        if (i !== idx) return item;
        if (field === '_replace') return { ...item, ...value };
        return { ...item, [field]: value };
      }),
    }));
  };

  const deleteRow = (section, idx) => {
    setParsedData((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== idx),
    }));
  };

  const handleConfirm = async () => {
    if (!onSubmit || !parsedData) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const result = await onSubmit({
        type: 'concert',
        data: stripInternalFields(parsedData),
      });
      setSubmitResult({ ok: true, result });
      setParsedData(null);
      setFileName('');
      setEditingCell(null);
      setEditingConcert(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setSubmitResult({ ok: false, error: err.message || 'Submission failed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const totalRows =
    (parsedData?.people?.length || 0) +
    (parsedData?.songs?.length || 0) +
    (parsedData?.concerts?.length || 0);

  const missingDateCount = (parsedData?.concerts || []).filter(
    (c) => !c.date,
  ).length;

  return (
    <div className="BulkUpload-typePane">
      <p className="AdminPortal-sectionLead">
        Upload an Excel file (.xlsx) using the MCG Jazz template. Fill out any
        combination of the People, Songs, and Concerts sheets.
      </p>

      {parseError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {parseError}
        </div>
      )}
      {submitResult?.ok && (
        <ConcertSubmitSummary summary={submitResult.result} />
      )}
      {submitResult && !submitResult.ok && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {submitResult.error}
        </div>
      )}

      {!parsedData && (
        <div className="BulkUpload-dropzone">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="BulkUpload-fileInput"
            id="bulk-upload-input-concert"
          />
          <label htmlFor="bulk-upload-input-concert" className="BulkUpload-dropLabel">
            {uploading ? (
              <span>Parsing {fileName}...</span>
            ) : (
              <>
                <span className="BulkUpload-dropIcon">+</span>
                <span>Click to select an .xlsx file</span>
                <span className="BulkUpload-dropHint">
                  Uses the MCG Jazz Upload Template format
                </span>
              </>
            )}
          </label>
        </div>
      )}

      {parsedData && (
        <>
          <div className="BulkUpload-fileBadge">
            <span>{fileName}</span>
            <button type="button" className="BulkUpload-clearBtn" onClick={handleReset}>
              Clear
            </button>
          </div>

          <div className="BulkUpload-summary">
            {parsedData.people.length > 0 && (
              <span className="BulkUpload-summaryChip">
                {parsedData.people.length} people
              </span>
            )}
            {parsedData.songs.length > 0 && (
              <span className="BulkUpload-summaryChip">
                {parsedData.songs.length} songs
              </span>
            )}
            {parsedData.concerts.length > 0 && (
              <span className="BulkUpload-summaryChip">
                {parsedData.concerts.length} concerts
              </span>
            )}
            {missingDateCount > 0 && (
              <span
                className="BulkUpload-summaryChip BulkUpload-summaryChip--warning"
                title="Concerts missing a date will be inserted, but future uploads of the same title without a date will merge into one event. Fill in a date to keep them distinct."
              >
                {missingDateCount} missing date
              </span>
            )}
          </div>

          {/* People table */}
          {parsedData.people.length > 0 && (
            <div className="BulkUpload-sheetSection">
              <h3 className="BulkUpload-sheetTitle">People</h3>
              <div className="AdminPortal-tableWrap">
                <table className="AdminPortal-table">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Instrument</th>
                      <th scope="col">Bio</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.people.map((p, i) => (
                      <tr key={p._idx}>
                        <td>
                          <EditableCell
                            value={p.name}
                            editing={editingCell?.key === `people-${i}-name`}
                            onStartEdit={() => setEditingCell({ key: `people-${i}-name` })}
                            onSave={(v) => { updateField('people', i, 'name', v); setEditingCell(null); }}
                            onCancel={() => setEditingCell(null)}
                          />
                        </td>
                        <td>
                          <EditableSelect
                            value={p.instrument}
                            options={INSTRUMENTS}
                            onChange={(v) => updateField('people', i, 'instrument', v)}
                          />
                        </td>
                        <td>
                          <EditableCell
                            value={p.bio}
                            editing={editingCell?.key === `people-${i}-bio`}
                            onStartEdit={() => setEditingCell({ key: `people-${i}-bio` })}
                            onSave={(v) => { updateField('people', i, 'bio', v); setEditingCell(null); }}
                            onCancel={() => setEditingCell(null)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ChatButton AdminPortal-delete"
                            onClick={() => deleteRow('people', i)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Songs table */}
          {parsedData.songs.length > 0 && (
            <div className="BulkUpload-sheetSection">
              <h3 className="BulkUpload-sheetTitle">Songs</h3>
              <div className="AdminPortal-tableWrap">
                <table className="AdminPortal-table">
                  <thead>
                    <tr>
                      <th scope="col">Title</th>
                      <th scope="col">Composer</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.songs.map((s, i) => (
                      <tr key={s._idx}>
                        <td>
                          <EditableCell
                            value={s.title}
                            editing={editingCell?.key === `songs-${i}-title`}
                            onStartEdit={() => setEditingCell({ key: `songs-${i}-title` })}
                            onSave={(v) => { updateField('songs', i, 'title', v); setEditingCell(null); }}
                            onCancel={() => setEditingCell(null)}
                          />
                        </td>
                        <td>
                          <EditableCell
                            value={s.composer}
                            editing={editingCell?.key === `songs-${i}-composer`}
                            onStartEdit={() => setEditingCell({ key: `songs-${i}-composer` })}
                            onSave={(v) => { updateField('songs', i, 'composer', v); setEditingCell(null); }}
                            onCancel={() => setEditingCell(null)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ChatButton AdminPortal-delete"
                            onClick={() => deleteRow('songs', i)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Concerts */}
          {parsedData.concerts.length > 0 && (
            <div className="BulkUpload-sheetSection">
              <h3 className="BulkUpload-sheetTitle">Concerts</h3>
              {parsedData.concerts.map((c, i) => (
                <ConcertCard
                  key={c._idx}
                  concert={c}
                  isEditing={editingConcert === i}
                  onEdit={() => setEditingConcert(editingConcert === i ? null : i)}
                  onSave={(updated) => {
                    updateField('concerts', i, '_replace', updated);
                    setEditingConcert(null);
                  }}
                  onCancel={() => setEditingConcert(null)}
                  onDelete={() => deleteRow('concerts', i)}
                />
              ))}
            </div>
          )}

          <div className="BulkUpload-actions">
            <button type="button" className="ChatButton" onClick={handleReset}>
              Cancel
            </button>
            <button
              type="button"
              className="ChatButton AdminPortal-submitBtn BulkUpload-confirmBtn"
              onClick={handleConfirm}
              disabled={totalRows === 0 || submitting}
            >
              {submitting
                ? 'Submitting...'
                : `Confirm & submit (${totalRows} record${totalRows !== 1 ? 's' : ''})`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Drop the `_idx` tracking keys the parser adds for React keys, and empty
 * strings for date so the server can coerce to null. Date validation on the
 * backend requires strict YYYY-MM-DD when provided.
 */
function stripInternalFields(parsed) {
  return {
    people: (parsed.people || []).map(({ _idx, ...rest }) => ({
      name: rest.name || '',
      instrument: rest.instrument || null,
      bio: rest.bio || null,
    })),
    songs: (parsed.songs || []).map(({ _idx, ...rest }) => ({
      title: rest.title || '',
      composer: rest.composer || null,
    })),
    concerts: (parsed.concerts || []).map(({ _idx, ...rest }) => ({
      title: rest.title || '',
      date: rest.date || null,
      location: rest.location || null,
      description: rest.description || null,
      performers: (rest.performers || []).map((p) => ({
        name: p.name || '',
        instrument: p.instrument || null,
      })),
      setlist: (rest.setlist || []).map((s) => ({
        title: s.title || '',
        trackNumber:
          typeof s.trackNumber === 'number' ? s.trackNumber : null,
      })),
    })),
  };
}

function ConcertSubmitSummary({ summary }) {
  if (!summary) return null;
  const stats = [
    ['People', summary.newPeople, summary.matchedPeople],
    ['Songs', summary.newSongs, summary.matchedSongs],
    ['Locations', summary.newLocations, summary.matchedLocations],
    ['Events', summary.newEvents, summary.matchedEvents],
  ];
  return (
    <div className="AdminPortal-banner AdminPortal-banner--success" role="status">
      <strong>
        {summary.dryRun ? 'Dry run complete (nothing saved).' : 'Upload saved.'}
      </strong>
      <ul className="BulkUpload-summaryList">
        {stats.map(([label, added, matched]) => (
          <li key={label}>
            {label}: <strong>{added}</strong> new, {matched} matched
          </li>
        ))}
        <li>
          Appearances: <strong>{summary.newAppearances}</strong> new,{' '}
          {summary.skippedDuplicateAppearances} already existed
        </li>
        <li>
          Setlist entries: <strong>{summary.newEventSongs}</strong> new,{' '}
          {summary.skippedDuplicateEventSongs} already existed
        </li>
        <li>
          Instrument roles: {summary.newRoles} new role{summary.newRoles === 1 ? '' : 's'},{' '}
          {summary.newPeopleRoles} person-role link
          {summary.newPeopleRoles === 1 ? '' : 's'}
        </li>
        {(summary.bioFillIns > 0 || summary.instrumentAdditions > 0) && (
          <li>
            Enrichment: {summary.bioFillIns} bio fill-in
            {summary.bioFillIns === 1 ? '' : 's'},{' '}
            {summary.instrumentAdditions} instrument addition
            {summary.instrumentAdditions === 1 ? '' : 's'}
          </li>
        )}
        {!summary.dryRun && (
          <li>
            Re-embedded:{' '}
            <strong>
              {summary.embeddedPeople +
                summary.embeddedSongs +
                summary.embeddedEvents}
            </strong>{' '}
            record
            {summary.embeddedPeople +
              summary.embeddedSongs +
              summary.embeddedEvents ===
            1
              ? ''
              : 's'}{' '}
            ({summary.embeddedPeople} people, {summary.embeddedSongs} songs,{' '}
            {summary.embeddedEvents} events)
            {summary.embeddingFailures > 0 && (
              <>
                {' '}
                — <strong>{summary.embeddingFailures}</strong> embedding failure
                {summary.embeddingFailures === 1 ? '' : 's'} (check server logs)
              </>
            )}
          </li>
        )}
      </ul>
      {summary.warnings?.length > 0 && (
        <div className="BulkUpload-summaryNote">
          {summary.warnings.map((w, i) => (
            <div key={i}>&bull; {w}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditableCell({ value, editing, onStartEdit, onSave, onCancel, className }) {
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <input
        type="text"
        className="AdminPortal-input BulkUpload-editInput"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onSave(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(draft);
          if (e.key === 'Escape') onCancel();
        }}
        autoFocus
      />
    );
  }

  return (
    <span
      className={`BulkUpload-cellValue ${className || ''}`}
      onClick={onStartEdit}
      title="Click to edit"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit();
        }
      }}
    >
      <span className="BulkUpload-cellText">
        {value || <span className="BulkUpload-emptyCell">-</span>}
      </span>
      <span className="BulkUpload-cellEditIcon" aria-hidden="true">✎</span>
    </span>
  );
}

function EditableSelect({ value, options, onChange }) {
  const lowered = typeof value === 'string' ? value.toLowerCase() : '';
  const match = options.find((o) => o === lowered);
  return (
    <select
      className="AdminPortal-select BulkUpload-inlineSelect"
      value={match || ''}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">--</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

function ConcertCard({ concert, isEditing, onEdit, onSave, onCancel, onDelete }) {
  const [draft, setDraft] = useState({ ...concert });
  const [newPerfName, setNewPerfName] = useState('');
  const [newPerfInst, setNewPerfInst] = useState('');
  const [newSongTitle, setNewSongTitle] = useState('');

  const updateDraft = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const removePerformer = (pi) => {
    setDraft((d) => ({ ...d, performers: d.performers.filter((_, i) => i !== pi) }));
  };

  const addPerformer = () => {
    if (!newPerfName.trim()) return;
    setDraft((d) => ({
      ...d,
      performers: [...d.performers, { name: newPerfName.trim(), instrument: newPerfInst.trim() }],
    }));
    setNewPerfName('');
    setNewPerfInst('');
  };

  const removeSong = (si) => {
    setDraft((d) => ({
      ...d,
      setlist: d.setlist.filter((_, i) => i !== si).map((s, i) => ({ ...s, trackNumber: i + 1 })),
    }));
  };

  const addSong = () => {
    if (!newSongTitle.trim()) return;
    setDraft((d) => ({
      ...d,
      setlist: [...d.setlist, { title: newSongTitle.trim(), trackNumber: d.setlist.length + 1 }],
    }));
    setNewSongTitle('');
  };

  if (isEditing) {
    return (
      <div className="BulkUpload-concertCard BulkUpload-concertCard--editing">
        <div className="BulkUpload-concertEditGrid">
          <label className="AdminPortal-field">
            <span className="AdminPortal-fieldLabel">Title</span>
            <input
              type="text"
              className="AdminPortal-input"
              value={draft.title}
              onChange={(e) => updateDraft('title', e.target.value)}
            />
          </label>
          <label className="AdminPortal-field">
            <span className="AdminPortal-fieldLabel">Date</span>
            <input
              type="date"
              className="AdminPortal-input"
              value={draft.date}
              onChange={(e) => updateDraft('date', e.target.value)}
            />
          </label>
          <label className="AdminPortal-field">
            <span className="AdminPortal-fieldLabel">Location</span>
            <input
              type="text"
              className="AdminPortal-input"
              value={draft.location}
              onChange={(e) => updateDraft('location', e.target.value)}
            />
          </label>
          <label className="AdminPortal-field">
            <span className="AdminPortal-fieldLabel">Description</span>
            <textarea
              className="AdminPortal-textarea"
              value={draft.description}
              onChange={(e) => updateDraft('description', e.target.value)}
              rows={2}
            />
          </label>
        </div>

        {/* Performers edit */}
        <div className="BulkUpload-concertSubsection">
          <span className="BulkUpload-subLabel">Performers</span>
          {draft.performers.map((p, pi) => (
            <div key={pi} className="BulkUpload-editChipRow">
              <span className="BulkUpload-chip">
                {p.name}{p.instrument ? ` - ${p.instrument}` : ''}
              </span>
              <button
                type="button"
                className="BulkUpload-chipRemove"
                onClick={() => removePerformer(pi)}
              >
                x
              </button>
            </div>
          ))}
          <div className="AdminPortal-addRow">
            <input
              type="text"
              className="AdminPortal-input"
              value={newPerfName}
              onChange={(e) => setNewPerfName(e.target.value)}
              placeholder="Name"
              onKeyDown={(e) => e.key === 'Enter' && addPerformer()}
            />
            <input
              type="text"
              className="AdminPortal-input"
              value={newPerfInst}
              onChange={(e) => setNewPerfInst(e.target.value)}
              placeholder="Instrument"
              onKeyDown={(e) => e.key === 'Enter' && addPerformer()}
            />
            <button type="button" className="ChatButton" onClick={addPerformer}>Add</button>
          </div>
        </div>

        {/* Setlist edit */}
        <div className="BulkUpload-concertSubsection">
          <span className="BulkUpload-subLabel">Setlist</span>
          {draft.setlist.map((s, si) => (
            <div key={si} className="BulkUpload-editChipRow">
              <span className="BulkUpload-chip">
                {s.trackNumber}. {s.title}
              </span>
              <button
                type="button"
                className="BulkUpload-chipRemove"
                onClick={() => removeSong(si)}
              >
                x
              </button>
            </div>
          ))}
          <div className="AdminPortal-addRow">
            <input
              type="text"
              className="AdminPortal-input"
              value={newSongTitle}
              onChange={(e) => setNewSongTitle(e.target.value)}
              placeholder="Song title"
              onKeyDown={(e) => e.key === 'Enter' && addSong()}
            />
            <button type="button" className="ChatButton" onClick={addSong}>Add</button>
          </div>
        </div>

        <div className="BulkUpload-concertEditActions">
          <button type="button" className="ChatButton" onClick={onCancel}>Cancel</button>
          <button type="button" className="ChatButton AdminPortal-submitBtn" onClick={() => onSave(draft)}>
            Save
          </button>
        </div>
      </div>
    );
  }

  const missingDate = !concert.date;

  return (
    <div
      className={`BulkUpload-concertCard${missingDate ? ' BulkUpload-concertCard--missingDate' : ''}`}
    >
      <div className="BulkUpload-concertHeader">
        <div className="BulkUpload-concertInfo">
          <span className="BulkUpload-concertTitle">{concert.title}</span>
          <span className="BulkUpload-concertMeta">
            {concert.date ? (
              <span>{concert.date}</span>
            ) : (
              <span
                className="BulkUpload-concertMissingDate"
                title="No date. Same-title concerts without dates will merge into a single event on re-upload."
              >
                ⚠ No date
              </span>
            )}
            {concert.location && <span> &middot; {concert.location}</span>}
          </span>
        </div>
        <div className="BulkUpload-concertActions">
          <button type="button" className="ChatButton" onClick={onEdit}>Edit</button>
          <button type="button" className="ChatButton AdminPortal-delete" onClick={onDelete}>Delete</button>
        </div>
      </div>

      {concert.performers.length > 0 && (
        <div className="BulkUpload-concertSubsection">
          <span className="BulkUpload-subLabel">Performers:</span>
          <div className="BulkUpload-chipList">
            {concert.performers.map((p, pi) => (
              <span key={pi} className="BulkUpload-chip">
                {p.name}{p.instrument ? ` - ${p.instrument}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {concert.setlist.length > 0 && (
        <div className="BulkUpload-concertSubsection">
          <span className="BulkUpload-subLabel">Setlist:</span>
          <div className="BulkUpload-chipList">
            {concert.setlist.map((s, si) => (
              <span key={si} className="BulkUpload-chip">
                {s.trackNumber}. {s.title}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
